import React, { useState, useEffect, useRef } from 'react';
import { artistsApi } from '../../repositories/artists';
import { Artist } from './types';
import { RequiredAsterisk } from '../common/RequiredAsterisk';

interface ArtistTypeaheadProps {
  value: string;
  onChange: (value: string, artistData?: Artist) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: string;
  touched?: boolean;
}

// Utility to strip leading/trailing quotes and whitespace
const cleanString = (value: any) =>
  typeof value === "string"
    ? value.trim().replace(/^"+|"+$/g, "")
    : value;

const ArtistTypeahead: React.FC<ArtistTypeaheadProps> = ({
  value,
  onChange,
  placeholder = "Enter artist name",
  className = "",
  disabled = false,
  error,
  touched
}) => {
  const [artists, setArtists] = useState<Artist[]>([]);
  const [filteredArtists, setFilteredArtists] = useState<Artist[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load artists on component mount
  useEffect(() => {
    loadArtists();
  }, []);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Simple filtering - no complex logic
  useEffect(() => {
    if (value && value.length > 0) {
      const filtered = artists.filter(artist =>
        artist.artistName.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredArtists(filtered);
    } else {
      setFilteredArtists([]);
    }
  }, [value, artists]);

  const loadArtists = async () => {
    try {
      setIsLoading(true);
      const response = await artistsApi.getAll({ limit: 100 });
      const artistsData = response.data.data.artists || [];
      setArtists(artistsData);
    } catch (error) {
      console.error('Error loading artists:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleArtistSelect = (artist: Artist) => {
    onChange(artist.artistName, artist);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Only handle specific navigation keys, let everything else pass through
    if (isOpen && filteredArtists.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < filteredArtists.length - 1 ? prev + 1 : prev
          );
          return;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
          return;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0) {
            handleArtistSelect(filteredArtists[selectedIndex]);
          }
          return;
      }
    }
    
    // Handle Escape key
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSelectedIndex(-1);
      return;
    }
    
    // For all other keys (including spaces), do nothing - let them pass through
  };

  return (
    <div className="relative" ref={dropdownRef}>

      {/* Input Field with Icon */}
      <div className="relative">
        <input
          type="text"
          id="artist-name"
          name="artistName"
          placeholder="Enter Artist Name"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${
            error && touched ? 'border-red-500' : 'border-gray-300'
          } ${className}`}
        />
        
        {/* Search Icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
          ) : (
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {isOpen && filteredArtists.length > 0 && (
        <div className="absolute z-50 w-full mt-1 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg max-h-60">
          {/* Header */}
          <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b bg-gray-50">
            <div className="flex items-center">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Artists from your roster
            </div>
          </div>

          {/* Artist List */}
          {filteredArtists.map((artist, index) => (
            <div
              key={artist.id}
              className={`px-3 py-3 cursor-pointer border-b border-gray-100 hover:bg-blue-50 transition-colors ${
                index === selectedIndex ? 'bg-blue-50' : ''
              }`}
              onClick={() => handleArtistSelect(artist)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{artist.artistName}</div>
                  <div className="flex items-center mt-1 space-x-3 text-xs text-gray-500">
                    {artist.spotifyURI && (
                      <span className="flex items-center">
                        <svg className="w-3 h-3 mr-1 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.84-.179-.84-.66 0-.359.24-.66.54-.78 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.242 1.021zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56z"/>
                        </svg>
                        Spotify
                      </span>
                    )}
                    {artist.hometown && (
                      <span className="flex items-center">
                        <svg className="w-3 h-3 mr-1 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/>
                        </svg>
                        {cleanString(artist.hometown).slice(0, 20)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-xs font-medium text-blue-600">
                  Auto-fill
                </div>
              </div>
            </div>
          ))}

          {/* Manual Entry Option */}
          <div className="px-3 py-3 text-sm text-gray-600 border-t bg-gray-50">
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              <span className="text-sm">Continue with "{value}" as new artist</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistTypeahead;